import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatterFileType, MatterSource } from '@models/matter';
import { BillingDefaultsSubTypesRepoService } from '@repos/billing/billing-defaults-sub-types.service';
import {
	BillingBusinessTypeIds,
	BillingSubType,
} from '@models/billingBusiness';
import { Logger } from '@core/logger';
import { Store } from '@ngxs/store';
import { UserProfileState } from '@state/user-profile';
import { BillingDefaultsApiService } from 'src/app/shared/api-repos/billing-defaults/billing-defaults.api.service';
import { BillingBusinessSubTypesRepoService } from '@repos/billing/billing-business-sub-types.service';
import { BillingBusinessApiService } from 'src/app/shared/api-repos/billing-business/billing-business.api.service';

@Injectable({
	providedIn: 'root',
})
export class BillingService {
	constructor(
		private billingBusinessSubTypesRepo: BillingBusinessSubTypesRepoService,
		private billingDefaultsSubTypesRepo: BillingDefaultsSubTypesRepoService,
		private store: Store,
		private billingDefaultsApiService: BillingDefaultsApiService,
		private billingBusinessApiService: BillingBusinessApiService,
	) {}

	public async getPrice(
		matterFileType: MatterFileType,
		matterSource: MatterSource,
	): Promise<number> {
		const notLoggedIn = !this.store.selectSnapshot(
			UserProfileState.getCurrentBusinessId,
		);
		const billingBusinessTypeId =
			matterSource === MatterSource.CLOSER
				? BillingBusinessTypeIds.MATTER
				: BillingBusinessTypeIds.LEAP_MATTER;

		const billingDefaultPrice = await this.getBillingDefaultFromBackend(
			matterFileType,
			billingBusinessTypeId,
		);
		if (notLoggedIn) {
			return billingDefaultPrice;
		}

		Logger.info(
			`Checking to see if price exists in ${this.billingBusinessSubTypesRepo.GetCollectionPath(billingBusinessTypeId)}.`,
		);

		const billingBusinessPrice = await this.getBillingBusinessFromBackend(
			matterFileType,
			billingBusinessTypeId,
		);

		if (billingBusinessPrice) {
			return billingBusinessPrice;
		}

		return billingDefaultPrice;
	}

	private async getBillingDefaultFromBackend(
		matterFileType: MatterFileType,
		billingBusinessTypeId: BillingBusinessTypeIds,
	) {
		Logger.info(
			`BusinessID has not been set yet so we will fetch prices for matter from our backend.`,
		);

		const subTypes: BillingSubType[] = await firstValueFrom(
			this.billingDefaultsApiService.getSubTypes(billingBusinessTypeId),
		);
		const subType = subTypes.find((x) => x.id === matterFileType);

		if (subType && subType.price) {
			Logger.info(
				`Returning price found in ${this.billingDefaultsSubTypesRepo.GetCollectionPath(billingBusinessTypeId)}.`,
			);
			return subType.price;
		}

		Logger.error(
			`Price not found in ${this.billingDefaultsSubTypesRepo.GetCollectionPath(billingBusinessTypeId)}.`,
		);
	}

	private async getBillingBusinessFromBackend(
		matterFileType: MatterFileType,
		billingBusinessTypeId: BillingBusinessTypeIds,
	) {
		Logger.info(
			`We will fetch prices from our backend for the current business.`,
		);

		const subTypes: BillingSubType[] = await firstValueFrom(
			this.billingBusinessApiService.getSubTypes(billingBusinessTypeId),
		);
		const subType = subTypes.find((x) => x.id === matterFileType);

		if (subType && subType.price) {
			Logger.info(
				`Returning price found in ${this.billingBusinessSubTypesRepo.GetCollectionPath(billingBusinessTypeId)}.`,
			);
			return subType.price;
		}

		Logger.info(
			`Price not found ${this.billingBusinessSubTypesRepo.GetCollectionPath(billingBusinessTypeId)}.`,
		);
	}
}
